/**
 * Approving a machine that has asked for this account's licence key (§13).
 *
 * It is a page rather than a banner, and that is deliberate: this is the one
 * screen in the account area where somebody hands a credential to something
 * outside the browser, and a decision like that should not sit in a strip above
 * a dashboard that carries on as though nothing were happening.
 *
 * What it shows is everything needed to answer "is this actually my machine?":
 * the name the machine gave itself, the country the request arrived from — the
 * half the machine cannot choose — the code, so it can be compared against the
 * one on screen over there, and which account is about to be used. Approving is
 * a person's act; nothing here happens on a timer.
 *
 * A code can also start on this side (§5.25): *Open in Airlock* offers one to
 * the signed-in account and hands it to the app as a link. Until the app claims
 * it there is nothing to approve, and this page waits for it. Once claimed it is
 * approved like any other — by the account that offered it and by no other; the
 * Worker refuses a different account, and its sentence is shown as it comes.
 *
 * And with the six digits the app shows once it has claimed the code
 * (`offered`). Being the account that offered is not enough: anybody can offer
 * a code to themselves and get somebody else to open the link, and approving it
 * would hand *their* machine this account's licence and organization. Only the
 * person in front of the app that claimed it can read the digits, so typing
 * them here is what ties the approval to the machine.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { Callout, Card, Empty, Field, GhostButton, Input, PrimaryButton } from '../components/primitives';
import { api, ApiError, apiPath } from '../lib/api';
import type { Me } from '../lib/types';

interface Grant {
  userCode: string;
  label: string | null;
  country: string | null;
  expiresAt: number;
  status: string;
  /** Claimed from an offered link: approving needs the digits the app shows. Absent from an older Worker. */
  offered?: boolean;
}

export function LinkMachine({ me, code, onDone }: { me: Me; code: string; onDone: () => void }): React.JSX.Element {
  const [grant, setGrant] = useState<Grant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answered, setAnswered] = useState<'approved' | 'denied' | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [confirmCode, setConfirmCode] = useState('');

  useEffect(() => {
    void api
      .get<{ grant: Grant }>(apiPath`/device/${code}`)
      .then((answer) => setGrant(answer.grant))
      .catch((failure) =>
        setError(failure instanceof ApiError ? failure.message : 'That code is not valid any more.')
      );
  }, [code]);

  // An offered code has not been claimed by an app yet, so there is nothing to
  // approve. It is read again until one has, which is the moment the app that
  // arrived by the link shows its label here.
  useEffect(() => {
    if (grant?.status !== 'offered') return;
    const poll = setInterval(() => {
      api
        .get<{ grant: Grant }>(apiPath`/device/${code}`)
        .then((answer) => setGrant(answer.grant))
        .catch(() => undefined);
    }, 2000);
    return () => clearInterval(poll);
  }, [grant?.status, code]);

  // The window is ten minutes and the number matters — somebody who walks away
  // mid-decision should see that it has run out rather than press a button that
  // quietly fails.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  const answer = async (approve: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const digits = confirmCode.replace(/\s+/g, '');
      await api.post('/device/approve', {
        userCode: code,
        approve,
        ...(approve && grant?.offered ? { confirmCode: digits } : {})
      });
      setAnswered(approve ? 'approved' : 'denied');
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  if (answered) {
    return (
      <Empty title={answered === 'approved' ? 'The machine has your licence' : 'Refused'}>
        {answered === 'approved'
          ? `Go back to ${grant?.label ?? 'the machine'} — it has already picked the key up, and nothing needs to be typed there.`
          : 'Nothing was handed over. If that was not you, no other action is needed: a refused code cannot be used.'}
        <div style={{ marginTop: 18 }}>
          <PrimaryButton onClick={onDone}>Done</PrimaryButton>
        </div>
      </Empty>
    );
  }

  if (error && !grant) {
    return (
      <Empty title="That code is no longer good">
        {error} Start again on the machine — a code lasts ten minutes and can be used once.
        <div style={{ marginTop: 18 }}>
          <PrimaryButton onClick={onDone}>Back to the account</PrimaryButton>
        </div>
      </Empty>
    );
  }

  if (!grant) return <Empty title="Reading that code…" />;

  if (grant.status === 'offered') {
    return (
      <Empty title="Waiting for Airlock to take this code">
        The code <span style={{ fontFamily: font.mono, letterSpacing: '.06em' }}>{grant.userCode}</span> was made for{' '}
        {me.user.email} and no app has claimed it yet. Allow your browser to open Airlock; this page moves on by itself
        once the app has it. If nothing happens in Airlock, update it — an older version does not take a code from a
        link — or link the machine from Airlock’s settings instead.
        <div style={{ marginTop: 18 }}>
          <GhostButton onClick={onDone}>Back to the account</GhostButton>
        </div>
      </Empty>
    );
  }

  const left = Math.max(0, grant.expiresAt - now);
  const minutes = Math.floor(left / 60_000);
  const seconds = Math.floor((left % 60_000) / 1000);

  return (
    <div style={{ maxWidth: 620 }}>
      <Card padding={28}>
        <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-.02em' }}>A machine is asking for your licence</div>
        <p style={{ fontSize: 14.5, color: color.inkSubtle, margin: '10px 0 24px', lineHeight: 1.6 }}>
          Approve it and Airlock on that machine is given your licence key. It receives the key and nothing else — no
          access to this account, and nothing that could sign in as you.
        </p>

        <div style={{ display: 'grid', gap: 12, fontSize: 14 }}>
          <Detail label="Machine">{grant.label ?? 'It did not give a name'}</Detail>
          <Detail label="Asking from">{grant.country ?? 'Unknown'}</Detail>
          <Detail label="Code">
            <span style={{ fontFamily: font.mono, fontSize: 15, letterSpacing: '.08em' }}>{grant.userCode}</span>
          </Detail>
          <Detail label="Your account">{me.user.email}</Detail>
          <Detail label="Expires in">
            {left === 0 ? 'expired' : `${minutes}:${String(seconds).padStart(2, '0')}`}
          </Detail>
        </div>

        <Callout tone="amber" title="Only approve what you started">
          The code above should match the one shown on the machine. If you did not just ask Airlock to link a machine,
          refuse this — somebody else is trying to use your licence.
        </Callout>

        {grant.offered && (
          <div style={{ margin: '0 0 18px' }}>
            <Field label="The six digits Airlock shows" htmlFor="link-confirm">
              <Input
                id="link-confirm"
                inputMode="numeric"
                autoComplete="off"
                value={confirmCode}
                onChange={(event) => setConfirmCode(event.target.value)}
                placeholder="123 456"
              />
            </Field>
            <p style={{ fontSize: 13, color: color.inkFaint, margin: '8px 0 0', lineHeight: 1.6 }}>
              Airlock shows them once it has taken the link. If Airlock shows no digits, you did not open this link
              from your own machine — refuse it.
            </p>
          </div>
        )}

        {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '0 0 14px' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <PrimaryButton size="lg" onClick={() => void answer(true)}>
            {busy ? 'Approving' : 'Approve this computer'}
          </PrimaryButton>
          <GhostButton size="lg" onClick={() => void answer(false)}>
            Refuse
          </GhostButton>
        </div>

        {!me.license && (
          <p style={{ fontSize: 13, color: color.inkFaint, margin: '18px 0 0', lineHeight: 1.6 }}>
            This account holds no licence yet, so approving hands over nothing — the machine will say so and offer to buy
            one. The trial it is already running is unaffected.
          </p>
        )}
      </Card>
    </div>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'baseline' }}>
      <span style={{ flex: 'none', width: 120, fontSize: 13, color: color.inkFaint }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );
}

/** The strip the signed-out screen shows, so signing in has a stated purpose. */
export function LinkNotice({ code }: { code: string }): React.JSX.Element {
  // A neutral note (§37.7, §10.6): nothing has gone wrong, and teal is never a note's tone.
  return (
    <div
      role="status"
      style={{
        background: color.offBg,
        color: color.offText,
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13,
        lineHeight: 1.5,
        margin: '0 0 20px'
      }}
    >
      Sign in to approve the machine showing{' '}
      <span style={{ fontFamily: font.mono, fontWeight: 500, letterSpacing: '.06em' }}>{code}</span>. It will be given your licence
      key and nothing else.
    </div>
  );
}
